/**
 * RecurringTasksPanel — Periodik tapşırıq qaydaları.
 * SuperAdmin only. List + create/edit + pause/resume + run-now.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Calendar, RefreshCw, Pause, Play, Trash2, Edit2, Search, Repeat, Info, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCHEDULE_LABELS = {
  daily: 'Hər gün',
  weekly: 'Hər həftə',
  monthly: 'Hər ay',
  yearly: 'İl bir dəfə',
};

const WEEKDAY_NAMES = ['B.E.', 'Ç.A.', 'Ç.', 'C.A.', 'C.', 'Ş.', 'B.'];

function describe(rule) {
  const st = rule.schedule_type;
  if (st === 'daily') return 'Hər gün';
  if (st === 'weekly') {
    const days = (rule.day_of_week || []).map((d) => WEEKDAY_NAMES[d]).join(', ');
    return `Hər həftə ${days || '—'}`;
  }
  if (st === 'monthly') {
    const s = rule.day_of_month_start || 1;
    const e = rule.day_of_month_end || s;
    return s === e ? `Hər ayın ${s}-ci günü` : `Hər ay ${s}-${e} arası`;
  }
  if (st === 'yearly') {
    return `İl bir dəfə ${rule.month_day}.${String(rule.month).padStart(2, '0')}`;
  }
  return '';
}

export default function RecurringTasksPanel() {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/recurring-tasks`, { headers }),
        axios.get(`${API}/admin/users`, { headers }).catch(() =>
          axios.get(`${API}/superadmin/users`, { headers })
        ),
        axios.get(`${API}/task-stages`, { headers }),
      ]);
      setRules(r1.data || []);
      setUsers((r2.data || []).filter((u) => u.is_active !== false));
      setStages(r3.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Yükləmə uğursuz');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (rule) => {
    try {
      await axios.post(`${API}/recurring-tasks/${rule.id}/toggle`, {}, { headers });
      toast.success(rule.active ? 'Dayandırıldı' : 'Aktiv edildi');
      fetchAll();
    } catch (e) {
      toast.error('Uğursuz');
    }
  };

  const handleRunNow = async (rule) => {
    try {
      const r = await axios.post(`${API}/recurring-tasks/${rule.id}/run-now`, {}, { headers });
      if (r.data?.created) {
        toast.success('Tapşırıq yaradıldı: ' + (r.data.task?.title || ''));
      } else {
        toast.info(r.data?.reason || 'Bu period üçün artıq yaradılıb');
      }
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Uğursuz');
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`«${rule.name}» qaydası silinsin?`)) return;
    try {
      await axios.delete(`${API}/recurring-tasks/${rule.id}`, { headers });
      toast.success('Silindi');
      fetchAll();
    } catch (e) {
      toast.error('Silinmədi');
    }
  };

  const handleSave = async (form) => {
    try {
      if (form.id) {
        const { id, ...payload } = form;
        await axios.put(`${API}/recurring-tasks/${id}`, payload, { headers });
        toast.success('Yeniləndi');
      } else {
        await axios.post(`${API}/recurring-tasks`, form, { headers });
        toast.success('Qayda yaradıldı');
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    }
  };

  return (
    <div className="space-y-4" data-testid="recurring-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Repeat className="h-5 w-5 text-purple-600" />
            Periodik Tapşırıqlar
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Avtomatik təkrarlanan tapşırıqlar — bir dəfə qur, sistem özü yaradacaq
          </p>
        </div>
        <Button
          onClick={() => setEditing({ __new: true })}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="add-rule-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Yeni qayda
        </Button>
      </div>

      {loading && <div className="text-sm text-zinc-500">Yüklənir...</div>}

      {rules.length === 0 && !loading && (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <Repeat className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <div className="text-sm text-zinc-500">
            Hələ heç bir periodik qayda yoxdur
          </div>
          <div className="text-xs text-zinc-400 mt-1">
            Məsələn: «Hər ayın 1-15 arası muhasibəyə ƏDV hesabatı»
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rules.map((r) => (
          <div
            key={r.id}
            className={`border rounded-lg p-4 ${r.active ? 'bg-white' : 'bg-zinc-50 opacity-60'}`}
            data-testid={`rule-card-${r.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {r.active ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">● Aktiv</Badge>
                  ) : (
                    <Badge className="bg-zinc-200 text-zinc-600 text-[10px]">Dayandırılıb</Badge>
                  )}
                  <span className="font-semibold text-sm">{r.name}</span>
                </div>
                <div className="text-xs text-zinc-600 mt-1.5">
                  📋 {r.title_template}
                </div>
                <div className="text-xs text-purple-700 mt-1 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {describe(r)}
                </div>
                {r.last_generated_at && (
                  <div className="text-[10px] text-zinc-500 mt-2">
                    Sonuncu yaradılma: {new Date(r.last_generated_at).toLocaleString('az-AZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-1 mt-3 pt-3 border-t">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleToggle(r)}
                className="text-xs"
                data-testid={`rule-toggle-${r.id}`}
              >
                {r.active ? <Pause className="h-3.5 w-3.5 mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
                {r.active ? 'Dayandır' : 'Aktiv et'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleRunNow(r)}
                className="text-xs"
                data-testid={`rule-run-now-${r.id}`}
                title="İndi yarat (test üçün)"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> İndi yarat
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing({ ...r })}
                className="text-xs"
                data-testid={`rule-edit-${r.id}`}
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(r)}
                className="text-xs text-red-600 hover:bg-red-50"
                data-testid={`rule-delete-${r.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <RuleEditDialog
          rule={editing}
          users={users}
          stages={stages}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ==================== Edit Dialog ====================
function RuleEditDialog({ rule, users, stages, onClose, onSave }) {
  const isNew = rule.__new;
  const [form, setForm] = useState({
    id: isNew ? null : rule.id,
    name: rule.name || '',
    title_template: rule.title_template || '',
    description_template: rule.description_template || '',
    schedule_type: rule.schedule_type || 'monthly',
    day_of_month_start: rule.day_of_month_start || 1,
    day_of_month_end: rule.day_of_month_end || 15,
    day_of_week: rule.day_of_week || [],
    month: rule.month || 1,
    month_day: rule.month_day || 1,
    start_date: rule.start_date || '',
    end_date: rule.end_date || '',
    assignee_ids: rule.assignee_ids || [],
    approver_ids: rule.approver_ids || [],
    priority: rule.priority || 'medium',
    tags: rule.tags || [],
    initial_stage_key: rule.initial_stage_key || (stages.find((s) => s.is_initial)?.key || ''),
    due_days_offset: rule.due_days_offset || 7,
    active: rule.active === undefined ? true : rule.active,
  });
  const [tagInput, setTagInput] = useState('');

  const toggleAssignee = (id) =>
    setForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(id)
        ? f.assignee_ids.filter((x) => x !== id)
        : [...f.assignee_ids, id],
    }));

  const toggleApprover = (id) =>
    setForm((f) => ({
      ...f,
      approver_ids: f.approver_ids.includes(id)
        ? f.approver_ids.filter((x) => x !== id)
        : [...f.approver_ids, id],
    }));

  const toggleDow = (d) =>
    setForm((f) => ({
      ...f,
      day_of_week: f.day_of_week.includes(d)
        ? f.day_of_week.filter((x) => x !== d)
        : [...f.day_of_week, d].sort(),
    }));

  const addTag = () => {
    const v = tagInput.trim();
    if (!v || form.tags.includes(v)) { setTagInput(''); return; }
    setForm({ ...form, tags: [...form.tags, v] });
    setTagInput('');
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('Ad boş ola bilməz'); return; }
    if (!form.title_template.trim()) { toast.error('Başlıq şablonu tələb olunur'); return; }
    onSave(form);
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rule-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Yeni periodik qayda' : 'Qayda redaktəsi'}</DialogTitle>
          <DialogDescription className="sr-only">Periodik tapşırıq qaydasının yaradılması və redaktəsi.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Qayda adı *</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="məs. Aylıq ƏDV hesabatı"
              data-testid="rule-name-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Yaradılacaq tapşırığın başlığı *</label>
            <Input
              value={form.title_template}
              onChange={(e) => setForm({ ...form, title_template: e.target.value })}
              placeholder="ƏDV hesabatı - {prev_month}"
              data-testid="rule-title-template"
            />
            <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
              <Info className="h-3 w-3" />
              Yer tutucular: <code className="bg-zinc-100 px-1">{'{prev_month}'}</code>{' '}
              <code className="bg-zinc-100 px-1">{'{curr_month}'}</code>{' '}
              <code className="bg-zinc-100 px-1">{'{curr_year}'}</code>{' '}
              <code className="bg-zinc-100 px-1">{'{today}'}</code>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Açıqlama (isteğe bağlı)</label>
            <Textarea
              value={form.description_template}
              onChange={(e) => setForm({ ...form, description_template: e.target.value })}
              rows={2}
              placeholder="..."
            />
          </div>

          {/* Schedule */}
          <div className="border rounded-lg p-3 bg-zinc-50 space-y-3">
            <div className="font-semibold text-sm">Cədvəl</div>
            <div>
              <label className="text-sm">Təkrarlanma növü</label>
              <Select value={form.schedule_type} onValueChange={(v) => setForm({ ...form, schedule_type: v })}>
                <SelectTrigger data-testid="rule-schedule-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Hər gün</SelectItem>
                  <SelectItem value="weekly">Hər həftə</SelectItem>
                  <SelectItem value="monthly">Hər ay (aralıq)</SelectItem>
                  <SelectItem value="yearly">İl bir dəfə</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.schedule_type === 'weekly' && (
              <div>
                <label className="text-sm block mb-1">Həftə günləri</label>
                <div className="flex gap-1 flex-wrap">
                  {WEEKDAY_NAMES.map((name, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleDow(idx)}
                      className={`px-3 py-1 text-xs rounded border ${
                        form.day_of_week.includes(idx)
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white text-zinc-600 border-zinc-300'
                      }`}
                      data-testid={`rule-dow-${idx}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {form.schedule_type === 'monthly' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm">Başlama günü</label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={form.day_of_month_start}
                    onChange={(e) => setForm({ ...form, day_of_month_start: parseInt(e.target.value) || 1 })}
                    data-testid="rule-dom-start"
                  />
                </div>
                <div>
                  <label className="text-sm">Son gün</label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={form.day_of_month_end}
                    onChange={(e) => setForm({ ...form, day_of_month_end: parseInt(e.target.value) || 1 })}
                    data-testid="rule-dom-end"
                  />
                </div>
                <div className="col-span-2 text-[11px] text-zinc-500 -mt-1">
                  ℹ️ Sistem bu aralıqda hər ay 1 dəfə tapşırıq yaradacaq (range başlama günündə)
                </div>
              </div>
            )}

            {form.schedule_type === 'yearly' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm">Ay (1-12)</label>
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    value={form.month}
                    onChange={(e) => setForm({ ...form, month: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <label className="text-sm">Gün</label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={form.month_day}
                    onChange={(e) => setForm({ ...form, month_day: parseInt(e.target.value) || 1 })}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-2 border-t">
              <div>
                <label className="text-xs text-zinc-600">Başlama tarixi</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-zinc-600">Bitmə tarixi (isteğe bağlı)</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Task properties */}
          <div className="border rounded-lg p-3 space-y-2">
            <div className="font-semibold text-sm">Tapşırığın özəllikləri</div>

            <div>
              <label className="text-sm">Məsul şəxslər</label>
              <div className="border rounded-md p-2 max-h-28 overflow-y-auto space-y-1 mt-1">
                {users.slice(0, 100).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.assignee_ids.includes(u.id)}
                      onChange={() => toggleAssignee(u.id)}
                      data-testid={`rule-assignee-${u.id}`}
                    />
                    <span className="truncate flex-1">{u.full_name || u.username}</span>
                    <span className="text-[10px] text-zinc-400">@{u.username}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm">Təsdiqləyənlər</label>
              <div className="border rounded-md p-2 max-h-24 overflow-y-auto space-y-1 mt-1">
                {users.slice(0, 100).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.approver_ids.includes(u.id)}
                      onChange={() => toggleApprover(u.id)}
                      data-testid={`rule-approver-${u.id}`}
                    />
                    <span className="truncate flex-1">{u.full_name || u.username}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs">İlkin etap</label>
                <Select value={form.initial_stage_key} onValueChange={(v) => setForm({ ...form, initial_stage_key: v })}>
                  <SelectTrigger data-testid="rule-initial-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {stages.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs">Prioritet</label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Aşağı</SelectItem>
                    <SelectItem value="medium">Orta</SelectItem>
                    <SelectItem value="high">Yüksək</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs">Müddət (gün)</label>
                <Input
                  type="number"
                  min="1"
                  value={form.due_days_offset}
                  onChange={(e) => setForm({ ...form, due_days_offset: parseInt(e.target.value) || 7 })}
                  data-testid="rule-due-offset"
                />
              </div>
            </div>

            <div>
              <label className="text-xs">Etiketlər</label>
              <div className="flex gap-1 flex-wrap mb-1">
                {form.tags.map((t) => (
                  <Badge key={t} variant="secondary" className="gap-1 text-xs">
                    {t}
                    <button onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-1">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="etiket"
                  className="text-xs h-7"
                />
                <Button size="sm" variant="outline" onClick={addTag}>+</Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv et</Button>
          <Button onClick={handleSubmit} className="bg-purple-600 hover:bg-purple-700" data-testid="rule-save-btn">
            Yadda saxla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
